import { UseFormReturn } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { DepositAmountTypeEnum } from '../../../lib/types/enums/payment-condition/deposit-amount-type.enum'
import { PaymentCondition } from '../../../lib/types/models/payment-condition/payment-condition'
import { PaymentConditionInput } from '../../../lib/types/models/payment-condition/payment-condition-input'
import { createDateStringSchema } from '../../../lib/utils/validations/date.validation'
import { createEnumSchema } from '../../../lib/utils/validations/enum-validation'
import {
  createNumberArraySchema,
  createNumberSchema
} from '../../../lib/utils/validations/int-array-validation'

export const PAYMENT_CONDITION_SCHEMA: ZodType<PaymentConditionInput> = z.object({
  depositType: createEnumSchema({ enumType: DepositAmountTypeEnum, fieldName: 'Type' }),
  depositPercentageOrAmountWithoutInsurance: createNumberSchema(
    'Pourcentage ou montant du dépôt sans assurance',
    { isInteger: true }
  ),
  secondPaymentDaysAfterBulletinValidation: createNumberSchema(
    '# jours après validation du bulletin pour la 2e échéance de paiement',
    { isPositive: true }
  ),
  secondPaymentPercentageWithoutInsurance: createNumberSchema(
    'Pourcentage du 2ème paiement sans assurance',
    { isInteger: true }
  ),
  balancePaymentDaysBeforeDeparture: createNumberSchema(
    '# jours avant le départ pour le paiement du solde',
    { isPositive: true, nullish: true }
  ),
  balancePaymentDeadline: createDateStringSchema({
    inputName: 'Date limite de paiement du solde'
  }).optional(),
  countries: createNumberArraySchema('Pays'),
  periods: createNumberArraySchema('Périodes')
})

export const mapPaymentConditionToInput = (
  paymentCondition: PaymentCondition
): PaymentConditionInput => ({
  depositPercentageOrAmountWithoutInsurance:
    paymentCondition.depositPercentageOrAmountWithoutInsurance,
  depositType: paymentCondition.depositType,
  secondPaymentDaysAfterBulletinValidation:
    paymentCondition.secondPaymentDaysAfterBulletinValidation,
  secondPaymentPercentageWithoutInsurance:
    paymentCondition.secondPaymentPercentageWithoutInsurance,
  balancePaymentDaysBeforeDeparture:
    paymentCondition.balancePaymentDaysBeforeDeparture ?? undefined,
  balancePaymentDeadline: paymentCondition.balancePaymentDeadline ?? undefined,
  periods: paymentCondition.periods.map((period) => period.id),
  countries: paymentCondition.countries.map((country) => country.id)
})

export const setBalancePaymentErrors = (
  error: 'both' | 'balancePaymentDeadline' | 'balancePaymentDaysBeforeDeparture',
  form: UseFormReturn<PaymentConditionInput, unknown, unknown>
) => {
  const message = 'Vous devez renseigner une date limite ou un nombre de jours avant le départ'
  if (error === 'both') {
    form.setError('balancePaymentDeadline', { type: 'custom', message })
    form.setError('balancePaymentDaysBeforeDeparture', { type: 'custom', message })
  }
  if (error === 'balancePaymentDeadline')
    form.setError('balancePaymentDeadline', { type: 'custom', message })
  if (error === 'balancePaymentDaysBeforeDeparture')
    form.setError('balancePaymentDaysBeforeDeparture', { type: 'custom', message })
}

export const paymentConditionValidations = (
  data: PaymentConditionInput,
  form: UseFormReturn<PaymentConditionInput, unknown, unknown>
) => {
  if (
    data.depositType === DepositAmountTypeEnum.PERCENTAGE &&
    data.depositPercentageOrAmountWithoutInsurance > 100
  ) {
    form.setError('depositPercentageOrAmountWithoutInsurance', {
      type: 'custom',
      message: 'Le pourcentage ne peut pas être supérieur à 100'
    })
  }
  if (data.depositPercentageOrAmountWithoutInsurance <= 0) {
    form.setError('depositPercentageOrAmountWithoutInsurance', {
      type: 'custom',
      message: 'Le pourcentage doit être supérieur à 0'
    })
  }
  if (
    data.secondPaymentPercentageWithoutInsurance > 100 ||
    data.secondPaymentPercentageWithoutInsurance < 0
  ) {
    form.setError('secondPaymentPercentageWithoutInsurance', {
      type: 'custom',
      message: 'Le pourcentage du 2ème paiement doit être compris entre 0 et 100'
    })
  }
  if (data.countries?.length === 0) {
    form.setError('countries', {
      type: 'custom',
      message: 'Vous devez renseigner au moins un pays'
    })
  }
  if (data.periods?.length === 0) {
    form.setError('periods', {
      type: 'custom',
      message: 'Vous devez renseigner au moins une période'
    })
  }
  if (
    !data.balancePaymentDeadline &&
    !data.balancePaymentDaysBeforeDeparture &&
    form.formState.isSubmitted
  ) {
    setBalancePaymentErrors('both', form)
  }
}
